/**
 * Cache Management Worker
 * Handles clearing browser cache, localStorage, sessionStorage, and Outlook add-in cache
 */

const CACHE_CLEARED_FLAG = 'xplan_cache_cleared';

/**
 * Clear all caches if not already cleared on this session
 * Clears: localStorage, sessionStorage, browser cache, and Outlook add-in cache
 */
export async function initializeCacheClearing(): Promise<void> {
	const cacheAlreadyCleared = localStorage.getItem(CACHE_CLEARED_FLAG) === 'true';
	
	if (!cacheAlreadyCleared) {
		if (process.env.NODE_ENV === "development") {
			console.log("[Cache] Clearing caches on app initialization");
		}
		
		// Clear browser cache using Service Worker
		clearBrowserCache();
		
		// Clear localStorage (but preserve important auth tokens)
		clearLocalStorage();
		
		// Clear sessionStorage
		clearSessionStorage();
		
		// Clear Outlook add-in cache
		await clearOutlookAddInCache();
		
		// Set flag to indicate cache has been cleared
		localStorage.setItem(CACHE_CLEARED_FLAG, 'true');
		
		if (process.env.NODE_ENV === "development") {
			console.log("[Cache] Cache clearing completed");
		}
	} else {
		if (process.env.NODE_ENV === "development") {
			console.log("[Cache] Cache already cleared in this session, skipping");
		}
	}
}

/**
 * Clear browser cache using Cache API and Service Workers
 */
function clearBrowserCache(): void {
	// Clear all caches using the Cache API
	if ('caches' in window) {
		caches.keys().then((cacheNames) => {
			cacheNames.forEach((cacheName) => {
				caches.delete(cacheName);
			});
		});
	}
	
	// Clear application cache if it exists
	if ('applicationCache' in window && window['applicationCache']) {
		try {
			const appCache = window['applicationCache'] as any;
			if (appCache && typeof appCache.update === 'function') {
				appCache.update();
			}
		} catch (e) {
			// Application cache not available or error occurred
		}
	}
}

/**
 * Clear localStorage while preserving auth tokens and essential data
 */
function clearLocalStorage(): void {
	// Preserve these keys during cache clear
	const preserveKeys = [
		'tfa_cookie', // Two-factor auth cookie
		'refresh_token', // SSO refresh token
		'msal_login_hint' // MSAL auth hint
	];
	
	// Save values of keys to preserve
	const preservedData: { [key: string]: string | null } = {};
	preserveKeys.forEach((key) => {
		const value = localStorage.getItem(key);
		if (value) {
			preservedData[key] = value;
		}
	});
	
	// Clear everything
	localStorage.clear();
	
	// Restore preserved data
	Object.entries(preservedData).forEach(([key, value]) => {
		if (value) {
			localStorage.setItem(key, value);
		}
	});
	
	// Re-set the cache cleared flag
	localStorage.setItem(CACHE_CLEARED_FLAG, 'true');
}

/**
 * Clear all sessionStorage
 */
function clearSessionStorage(): void {
	sessionStorage.clear();
}

/**
 * Clear Outlook add-in specific cache
 * This handles cached data that Office.js might be storing
 */
async function clearOutlookAddInCache(): Promise<void> {
	try {
		// Clear roaming settings cache in Outlook
		if (typeof Office !== 'undefined' && Office.context && Office.context.roamingSettings) {
			// Get all keys in roaming settings
			const keys = Office.context.roamingSettings.get('__keys__') || [];
			if (Array.isArray(keys)) {
				keys.forEach((key) => {
					try {
						Office.context.roamingSettings.remove(key);
					} catch (e) {
						// Key may not exist
					}
				});
			}
			
			// Save to persist the changes
			try {
				Office.context.roamingSettings.saveAsync();
			} catch (e) {
				// Save may fail if user is offline
			}
		}
		
		if (process.env.NODE_ENV === "development") {
			console.log("[Cache] Outlook add-in cache cleared");
		}
	} catch (error) {
		if (process.env.NODE_ENV === "development") {
			console.log("[Cache] Could not clear Outlook cache (may not be available):", error);
		}
		// Silently fail if Outlook API not available or error occurs
	}
}

/**
 * Reset the cache cleared flag (for debugging/testing)
 */
export function resetCacheFlag(): void {
	localStorage.removeItem(CACHE_CLEARED_FLAG);
	if (process.env.NODE_ENV === "development") {
		console.log("[Cache] Cache flag reset - cache will be cleared on next app load");
	}
}

/**
 * Check if cache has already been cleared this session
 */
export function isCacheCleared(): boolean {
	return localStorage.getItem(CACHE_CLEARED_FLAG) === 'true';
}
